<script setup lang="ts">
import { RouterLink } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/authStore'
import { useLocalizedRouter } from '../composables/useLocalizedRouter'
import { computed } from 'vue'
import { useAppointmentStore } from '../stores/appointmentStore'

const appointmentStore = useAppointmentStore()

const props = defineProps<{
  isOpen: boolean
}>()

const emit = defineEmits<{
  close: []
}>()

const { t } = useI18n()
const router = useRouter()
const authStore = useAuthStore()
const { localizedPath } = useLocalizedRouter()

const isAuthenticated = computed(() => authStore.isAuthenticated)

const logout = async () => {
  await authStore.logout()
  emit('close')
  // Rediriger vers la homepage en remplaçant l'historique
  router.replace(localizedPath('/')).catch(() => {
    // Si la redirection échoue, forcer le rechargement vers la homepage
    window.location.href = localizedPath('/')
  })
}

const closeMenu = () => {
  emit('close')
}

const handleLogoClick = () => {
  closeMenu()
  // S'assurer que la navigation vers la homepage fonctionne
  router.push(localizedPath('/')).catch(() => {
    window.location.href = localizedPath('/')
  })
}
</script>

<template>
  <div v-if="isOpen" class="mobile-nav-overlay" @click="closeMenu"></div>
  
  <nav class="mobile-nav" :class="{ 'mobile-nav-open': isOpen }">
    <div class="mobile-nav-header">
      <a href="/" class="mobile-nav-logo" @click.prevent="handleLogoClick">
        <div class="mobile-logo-icon">
          <i class="fas fa-code"></i>
        </div>
        <span class="mobile-logo-text">PostDev</span>
      </a>
      <button class="mobile-nav-close" @click="closeMenu" aria-label="Fermer le menu">
        <i class="fas fa-times"></i>
      </button>
    </div>

    <div class="mobile-nav-content">
      <div class="mobile-nav-links">
        <!-- Pages publiques -->
        <div class="mobile-nav-section">
          <p class="mobile-nav-section-title">{{ t('nav.navigation') }}</p>
          <RouterLink :to="localizedPath('/')" class="mobile-nav-link" @click="closeMenu">
            <i class="fas fa-home"></i>
            <span>{{ t('nav.home') }}</span>
          </RouterLink>
          <RouterLink :to="localizedPath('/services')" class="mobile-nav-link" @click="closeMenu">
            <i class="fas fa-cogs"></i>
            <span>{{ t('nav.services') }}</span>
          </RouterLink>
          <RouterLink :to="localizedPath('/portfolio')" class="mobile-nav-link" @click="closeMenu">
            <i class="fas fa-briefcase"></i>
            <span>{{ t('nav.portfolio') }}</span>
          </RouterLink>
          <RouterLink :to="localizedPath('/blog')" class="mobile-nav-link" @click="closeMenu">
            <i class="fas fa-blog"></i>
            <span>{{ t('nav.blog') }}</span>
          </RouterLink>
          <RouterLink :to="localizedPath('/outils')" class="mobile-nav-link" @click="closeMenu">
            <i class="fas fa-wrench"></i>
            <span>{{ t('nav.tools') }}</span>
          </RouterLink>
          <RouterLink :to="localizedPath('/contact')" class="mobile-nav-link" @click="closeMenu">
            <i class="fas fa-envelope"></i>
            <span>{{ t('nav.contact') }}</span>
          </RouterLink>
        </div>

        <!-- Pages utilisateur connecté -->
        <div class="mobile-nav-section" v-if="isAuthenticated">
          <p class="mobile-nav-section-title">{{ t('nav.mySpace') }}</p>
          <RouterLink :to="localizedPath('/account')" class="mobile-nav-link" @click="closeMenu">
            <i class="fas fa-user-circle"></i>
            <span>{{ t('nav.account') }}</span>
          </RouterLink>
          <RouterLink :to="localizedPath('/dashboard')" class="mobile-nav-link" @click="closeMenu">
            <i class="fas fa-tachometer-alt"></i>
            <span>{{ t('nav.dashboard') }}</span>
          </RouterLink>
          <RouterLink :to="localizedPath('/my-projects')" class="mobile-nav-link" @click="closeMenu">
            <i class="fas fa-project-diagram"></i>
            <span>{{ t('nav.myProjects') }}</span>
          </RouterLink>
        </div>

        <!-- Pages non authentifié -->
        <div class="mobile-nav-section" v-else>
          <p class="mobile-nav-section-title">{{ t('nav.accountSection') }}</p>
          <RouterLink :to="localizedPath('/auth/login')" class="mobile-nav-link" @click="closeMenu">
            <i class="fas fa-sign-in-alt"></i>
            <span>{{ t('nav.login') }}</span>
          </RouterLink>
          <RouterLink :to="localizedPath('/auth/signup')" class="mobile-nav-link" @click="closeMenu">
            <i class="fas fa-user-plus"></i>
            <span>{{ t('nav.register') }}</span>
          </RouterLink>
        </div>

        <!-- Bouton Réserver un appel -->
        <button class="mobile-action-btn mobile-btn-primary" @click="closeMenu(); appointmentStore.openModal()">
          <i class="fas fa-phone-alt"></i>
          <span>{{ t('nav.bookCall') }}</span>
        </button>

        <!-- Déconnexion (tout en bas) -->
        <template v-if="isAuthenticated">
          <button class="mobile-action-btn mobile-btn-gray" @click="logout">
            <i class="fas fa-sign-out-alt"></i>
            <span>{{ t('nav.logout') }}</span>
          </button>
        </template>
      </div>
    </div>
  </nav>
</template>

<style scoped>
.mobile-nav-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  animation: fadeIn 0.3s ease;
}

.mobile-nav {
  position: fixed;
  top: 0;
  right: -100%;
  width: 85%;
  max-width: 380px;
  height: 100vh;
  background: white;
  z-index: 1000;
  transition: right 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: -5px 0 30px rgba(0, 0, 0, 0.15);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
}

.mobile-nav-open {
  right: 0;
}

.mobile-nav-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 2px solid rgba(var(--primary-color-rgb), 0.1);
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
}

.mobile-nav-logo {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  text-decoration: none;
  color: var(--primary-color);
}

.mobile-logo-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  background: var(--primary-color);
  border-radius: 50%;
  color: white;
  font-size: 1.25rem;
  transition: all 0.3s ease;
}

.mobile-logo-text {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-color);
}

.mobile-nav-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: var(--text-primary);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
}

.mobile-nav-close:hover {
  background: rgba(var(--primary-color-rgb), 0.1);
  color: var(--primary-color);
}

.mobile-nav-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  overflow-y: auto;
}

.mobile-nav-links {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.mobile-nav-section {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 1.5rem;
}

.mobile-nav-section:last-of-type {
  margin-bottom: 0;
}

.mobile-nav-section-title {
  color: var(--text-secondary);
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
  padding: 0 1rem;
}

.mobile-nav-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  text-decoration: none;
  color: var(--text-primary);
  font-weight: 500;
  font-size: 1rem;
  border-radius: 8px;
  transition: all 0.25s ease;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  min-height: auto;
}

.mobile-nav-link i {
  width: 20px;
  text-align: center;
  color: var(--primary-color);
  font-size: 1rem;
  flex-shrink: 0;
}

.mobile-nav-link:hover,
.mobile-nav-link.router-link-active {
  color: var(--primary-dark);
  background: rgba(var(--primary-color-rgb), 0.1);
  padding-left: 1.15rem;
}

.mobile-nav-link.router-link-active {
  font-weight: 600;
  color: var(--primary-dark);
}


.mobile-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
}

.mobile-btn-primary {
  background: var(--primary-color);
  color: white;
}

.mobile-btn-primary:hover {
  background: var(--primary-dark);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(var(--primary-color-rgb), 0.3);
}

.mobile-btn-gray {
  background: var(--text-secondary);
  color: white;
}

.mobile-btn-gray:hover {
  background: #555555;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 102, 102, 0.3);
}

.mobile-btn-secondary {
  background: transparent;
  color: var(--primary-color);
  border: 2px solid var(--primary-color);
}

.mobile-btn-secondary:hover {
  background: var(--primary-color);
  color: white;
}

.mobile-btn-logout {
  background: transparent;
  color: #dc2626;
  border: 2px solid #dc2626;
}

.mobile-btn-logout:hover {
  background: #dc2626;
  color: white;
}


.mobile-action-btn {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.mobile-nav-link-button {
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  width: 100%;
  color: #dc2626;
}

.mobile-nav-link-button:hover {
  color: #dc2626;
  background: rgba(220, 38, 38, 0.05);
}

.mobile-nav-link-button i {
  color: #dc2626;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>

